import { useMutation, useQuery } from "react-query";
import { useAuth } from "../../../context";
import { FoundationDataV2 } from "../../../api";
import { handleFileDownload } from "../../../utils";

/*
  Keyed on the DATA YEAR ID, not the calendar year, wherever the server is.

  A reporting year holds one row per workstream, so caching on the year would have served the GHG table's
  rows to the Targets table. Each factory still answers a bare prefix when called with nothing, which is what
  lets a write invalidate every workstream at once.
*/
export const foundationV2Keys = {
  round: () => ["foundationV2Round"],
  years: () => ["foundationV2Years"],
  rows: (dataYearId) =>
    dataYearId === undefined ? ["foundationV2Rows"] : ["foundationV2Rows", dataYearId],
  runs: (dataYearId) =>
    dataYearId === undefined ? ["foundationV2Runs"] : ["foundationV2Runs", dataYearId],
  curated: (dataYearId) =>
    dataYearId === undefined ? ["foundationV2Curated"] : ["foundationV2Curated", dataYearId],
  targetStructure: (questionGroup) =>
    questionGroup === undefined
      ? ["foundationV2TargetStructure"]
      : ["foundationV2TargetStructure", questionGroup],
  curatedForYear: (year) =>
    year === undefined ? ["foundationV2CuratedForYear"] : ["foundationV2CuratedForYear", year],
  curatedUsage: (dataYearId) =>
    dataYearId === undefined
      ? ["foundationV2CuratedUsage"]
      : ["foundationV2CuratedUsage", dataYearId],
  datapointUsage: (uid) => ["foundationV2DatapointUsage", uid],
  pending: (dataYearId) =>
    dataYearId === undefined ? ["foundationV2Pending"] : ["foundationV2Pending", dataYearId],
  /*
    Keyed on the SORTED uid list, so asking about the same set twice hits the cache while asking about a
    different set does not. Unsorted, a caller that happened to list two figures the other way round would
    be served an answer computed for a different question — which here means a count of requirements.
  */
  answerImpact: (uids) => ["foundationV2AnswerImpact", [...(uids ?? [])].sort().join(",")],
  questionUsage: (questionId) => ["foundationV2QuestionUsage", questionId],
  curatedSaves: (dataYearId) =>
    dataYearId === undefined
      ? ["foundationV2CuratedSaves"]
      : ["foundationV2CuratedSaves", dataYearId],
};

/*
  Note the no-param calls are `callApi(FoundationDataV2.x)` with NO second argument. Passing a
  placeholder would put the access token into the params slot and send `Bearer undefined`, which surfaces
  as HTTP 500 "oidcIssuerLocation cannot be empty" — it reads like a backend OIDC misconfiguration and is
  not one.
*/

export function useFoundationV2Round(config) {
  const { callApi } = useAuth();
  return useQuery(foundationV2Keys.round(), () => callApi(FoundationDataV2.getRound), config);
}

export function useInitFoundationV2Round(config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(FoundationDataV2.initRound), config);
}

export function useSetFoundationV2Controllers(config) {
  const { callApi } = useAuth();
  return useMutation((ownerEmails) => callApi(FoundationDataV2.setControllers, ownerEmails), config);
}

export function useFoundationV2Years(config) {
  const { callApi } = useAuth();
  return useQuery(foundationV2Keys.years(), () => callApi(FoundationDataV2.listYears), config);
}

export function useCreateFoundationV2Year(config) {
  const { callApi } = useAuth();
  return useMutation((year) => callApi(FoundationDataV2.createYear, year), config);
}

/** `mutate({ year, entityId })` — one object, because callApi passes exactly one params argument. */
export function useAddFoundationV2EntityToYear(config) {
  const { callApi } = useAuth();
  return useMutation((params) => callApi(FoundationDataV2.addEntityToYear, params), config);
}

/** Removes a source document and its extraction from a workstream (ESG-4022). `{ dataYearId, fileInfoId }`. */
export function useRemoveFoundationV2Source(config) {
  const { callApi } = useAuth();
  return useMutation((params) => callApi(FoundationDataV2.removeSource, params), config);
}

/**
 * One workstream's target structure, or null.
 *
 * Per (organisation, workstream) and NOT per year: a reporting template belongs to the organisation, and each
 * figure records which VERSION of it it was matched against. Per workstream because the datapoints an
 * organisation wants for its targets are not the ones it wants for its emissions.
 */
export function useFoundationV2TargetStructure(questionGroup, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.targetStructure(questionGroup),
    () => callApi(FoundationDataV2.getTargetStructure, questionGroup),
    { enabled: !!questionGroup, ...config }
  );
}

export function useUploadFoundationV2TargetStructure(config) {
  const { callApi } = useAuth();
  return useMutation(
    (params) => callApi(FoundationDataV2.uploadTargetStructure, params),
    config
  );
}

/**
 * Downloads the blank template and hands it to the browser.
 *
 * The filename comes from `Content-Disposition`, with a fallback that MUST carry the extension — a
 * cross-origin response has that header stripped unless `SecurityConfig` exposes it, and a downloaded
 * file with no extension will not open.
 */
export function useDownloadFoundationV2TargetTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(
    (questionGroup) =>
      callApi(FoundationDataV2.downloadTargetStructureTemplate, questionGroup).then(
        ({ data, headers }) =>
          handleFileDownload(data, headers, "foundation-target-structure.xlsx")
      ),
    config
  );
}

export function useFoundationV2Rows(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.rows(dataYearId),
    () => callApi(FoundationDataV2.listRows, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

export function useFoundationV2CuratedRows(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.curated(dataYearId),
    () => callApi(FoundationDataV2.listCuratedRows, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

/**
 * Every workstream's approved figures for one reporting year.
 *
 * Its own key, separate from `curated(dataYearId)`: this answers a question about the year, so caching it under
 * one workstream's id would serve a partial set to a caller asking for all of it.
 */
export function useFoundationV2CuratedRowsForYear(year, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.curatedForYear(year),
    () => callApi(FoundationDataV2.listCuratedRowsForYear, year),
    { enabled: year !== undefined && year !== null, ...config }
  );
}

/**
 * Usage badges for one year's datapoints.
 *
 * Its own query rather than folded into the rows: the rows come from the foundation data, this comes from
 * the ISSB questions, and a question being answered elsewhere in the app should refresh the badge without
 * refetching the table.
 */
export function useFoundationV2CuratedUsage(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.curatedUsage(dataYearId),
    () => callApi(FoundationDataV2.listCuratedUsage, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

/**
 * The changes waiting for this workstream's owner to approve them (ESG-4012). Until then the rest of the
 * platform reads the last approved figures, so this is what the page has to show for an edit to be visible
 * as not yet live.
 */
export function useFoundationV2PendingChanges(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.pending(dataYearId),
    () => callApi(FoundationDataV2.listPendingChanges, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

/** The questions answered from one datapoint. Enabled only once a uid is chosen, so opening the panel is
 *  what fetches it rather than rendering the table. */
export function useFoundationV2DatapointUsage(datapointUid, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.datapointUsage(datapointUid),
    () => callApi(FoundationDataV2.getDatapointUsage, datapointUid),
    { enabled: !!datapointUid, ...config }
  );
}

/**
 * The requirements that would be affected by changing a set of figures.
 *
 * Deliberately NOT a plain `useQuery` fired on render: the pre-flight is asked at the moment the user
 * confirms a change, and the answer decides whether a modal opens at all. So this is a mutation — one call,
 * at a known moment, whose result the caller holds. A query keyed on the uid list would refetch on every
 * table render and would have to be enabled on a uid set that does not exist yet.
 */
export function useFoundationV2AnswerImpact(config) {
  const { callApi } = useAuth();
  return useMutation(
    (datapointUids) => callApi(FoundationDataV2.getAnswerImpact, datapointUids),
    config
  );
}

/** The foundation figures behind one question's answer, and whether any has moved since. */
export function useFoundationV2QuestionUsage(questionId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.questionUsage(questionId),
    () => callApi(FoundationDataV2.getQuestionFoundationUsage, questionId),
    { enabled: !!questionId, ...config }
  );
}

export function useFoundationV2CuratedSaves(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.curatedSaves(dataYearId),
    () => callApi(FoundationDataV2.listCuratedSaves, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

/**
 * Writes a curated set, or previews one with `dryRun`.
 *
 * A single object argument because `callApi` passes exactly one — `callApi(method, a, b)` would drop `b`.
 */
export function useSaveFoundationV2Curated(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ dataYearId, request, dryRun }) =>
      callApi(FoundationDataV2.saveCurated, { dataYearId, request, dryRun }),
    config
  );
}

export function useFoundationV2Runs(dataYearId, config) {
  const { callApi } = useAuth();
  return useQuery(
    foundationV2Keys.runs(dataYearId),
    () => callApi(FoundationDataV2.listRuns, dataYearId),
    { enabled: dataYearId !== undefined && dataYearId !== null, ...config }
  );
}

/*
  Row mutations. Each takes ONE argument because callApi(method, params) drops anything after the first —
  so patchRow's id and body travel together in a single object.
*/

/*
  Curated writes invalidate the curated rows AND the year list, because the year badge now carries the
  foundation-data count. Invalidating only the rows would leave that badge stale — the same reason the raw
  writes did it before ESG-3992 removed them.
*/
export function useAddFoundationV2CuratedRow(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ dataYearId, row }) => callApi(FoundationDataV2.addCuratedRow, { dataYearId, row }),
    config
  );
}

export function usePatchFoundationV2CuratedRow(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(FoundationDataV2.patchCuratedRow, { id, data }), config);
}

export function useDeleteFoundationV2CuratedRow(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(FoundationDataV2.deleteCuratedRow, id), config);
}

/** Promotes one extraction unchanged, or previews it. A single object because `callApi` passes one arg. */
export function useAcceptFoundationV2Run(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ runId, dryRun, expectedReplacedRows, keepOffSchema }) =>
      callApi(FoundationDataV2.acceptRun, { runId, dryRun, expectedReplacedRows, keepOffSchema }),
    config
  );
}
